import {useCallback, useEffect, useRef, useState} from 'react';
import {Skeleton, view, Icon, Action} from '@sail/ui';
import {clipboard} from '@sail/icons/react/Icon';
import {message, useIntl} from '@sail/intl';
import {QrCode} from 'src/v2/components/common/QrCode';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {LabelValueCopyRow} from 'src/v2/components/common/LabelValueCopyRow';
import {InactivityOverlay} from 'src/v2/components/anyCrypto/deposit/InactivityOverlay';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {getNetworkIcon} from 'src/components/Wallet/constants';
import {isMobile} from 'src/utils/utils';
import {CountdownRing} from 'src/v2/components/common/CountdownRing';
import {ExpandableDepositDetails} from 'src/v2/components/anyCrypto/deposit/ExpandableDepositDetails';
import type {SupportedNetwork} from 'src/types';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum} from '@stripe-internal/data-v1/schema/types';

const messages = {
  refreshCountdown: message<{
    seconds: number;
  }>({
    id: 'crypto.payins.v2.any_crypto.deposit.refresh_countdown',
    description: 'Countdown timer for quote refresh',
    defaultMessage: 'Refresh in {seconds}s',
  }),
  amountLabel: message({
    id: 'crypto.payins.v2.any_crypto.deposit.amount_label',
    description: 'Label for the deposit amount',
    defaultMessage: 'Amount',
  }),
  addressLabel: message({
    id: 'crypto.payins.v2.any_crypto.deposit.address_label',
    description: 'Label for the deposit address',
    defaultMessage: 'Address',
  }),
  copyAddress: message({
    id: 'crypto.payins.v2.any_crypto.deposit.copy_address',
    description: 'Button to copy the deposit address',
    defaultMessage: 'Copy address',
  }),
  copied: message({
    id: 'crypto.payins.v2.any_crypto.deposit.copied',
    description: 'Button text after address is copied',
    defaultMessage: 'Copied!',
  }),
  checkPaymentStatus: message({
    id: 'crypto.payins.v2.any_crypto.deposit.check_payment_status',
    description: 'Link to check payment processing status',
    defaultMessage: 'Check payment status',
  }),
};

const QR_CODE_SIZE = 156;
const QR_CODE_CONTAINER_PADDING_PX = 8;
const QR_CODE_CONTAINER_SIZE = QR_CODE_SIZE + 2 * QR_CODE_CONTAINER_PADDING_PX;

export const AnyCryptoDepositScreen = ({
  depositAddress,
  amount,
  token,
  network,
  onCheckPaymentStatus,
  isInactive,
  onResume,
  refreshCountdown,
}: {
  depositAddress: string | null;
  amount: string | null;
  token: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;
  network: SupportedNetwork | null;
  onCheckPaymentStatus?: () => void;
  isInactive: boolean;
  onResume: () => void;
  refreshCountdown: {
    secondsUntilRefresh: number;
    totalSeconds: number;
  } | null;
}) => {
  const intl = useIntl();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(() => {
    if (depositAddress) {
      // eslint-disable-next-line @sail/ui-no-navigator-clipboard
      navigator.clipboard.writeText(depositAddress);
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
      setIsCopied(true);
      copyTimerRef.current = setTimeout(() => setIsCopied(false), 2000);
    }
  }, [depositAddress]);

  const handleCopyAmount = useCallback(() => {
    if (amount) {
      // eslint-disable-next-line @sail/ui-no-navigator-clipboard
      navigator.clipboard.writeText(amount);
    }
  }, [amount]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const formattedToken = formatAssetCode(token);
  const networkIcon = network ? getNetworkIcon(network) : null;
  const mobile = isMobile();

  const countdown = refreshCountdown ? (
    <view.div
      css={{
        stack: 'x',
        gap: 'small',
        alignY: 'center',
        ...(!mobile ? {alignX: 'center'} : {}),
      }}
    >
      <CountdownRing
        seconds={refreshCountdown.secondsUntilRefresh}
        total={refreshCountdown.totalSeconds}
        size={10}
        strokeWidth={1.5}
      />
      <Text type="caption" css={{color: 'subdued'}}>
        {intl.formatMessage(messages.refreshCountdown, {
          seconds: refreshCountdown.secondsUntilRefresh,
        })}
      </Text>
    </view.div>
  ) : null;

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <ExpandableDepositDetails
        amount={amount}
        token={token}
        network={network}
      />

      <view.div
        css={{
          position: 'relative',
          borderRadius: 'small',
          borderColor: 'border',
          borderWidth: 'small',
          borderStyle: 'solid',
          overflow: 'hidden',
        }}
      >
        {isInactive ? <InactivityOverlay onResume={onResume} /> : null}
        <view.div
          css={{
            stack: 'y',
            paddingY: 'large',
            paddingX: 'space.150',
            gap: 'medium',
            ...(isInactive ? {filter: 'blur(6px)', pointerEvents: 'none'} : {}),
          }}
        >
          {!mobile ? (
            <>
              <view.div css={{alignSelfX: 'center'}}>
                {depositAddress ? (
                  <view.div
                    css={{
                      stack: 'z',
                      borderRadius: 'large',
                      padding: 'small',
                      backgroundColor: 'white',
                    }}
                  >
                    <QrCode
                      qrCodeUri={depositAddress}
                      size={QR_CODE_SIZE}
                      excavateSize={36}
                    />
                    {networkIcon ? (
                      <view.div
                        css={{
                          alignSelfX: 'center',
                          alignSelfY: 'center',
                          backgroundColor: 'white',
                          borderRadius: 'xsmall',
                          padding: 'space.175',
                        }}
                      >
                        <Icon
                          icon={networkIcon}
                          css={{
                            width: 'xlarge',
                            height: 'xlarge',
                            borderRadius: 'xsmall',
                          }}
                        />
                      </view.div>
                    ) : null}
                  </view.div>
                ) : (
                  <Skeleton
                    css={{
                      width: `${QR_CODE_CONTAINER_SIZE}px`,
                      height: `${QR_CODE_CONTAINER_SIZE}px`,
                    }}
                  />
                )}
              </view.div>
              {countdown}
            </>
          ) : null}

          <view.div css={{stack: 'y', gap: 'medium'}}>
            <LabelValueCopyRow
              label={intl.formatMessage(messages.amountLabel)}
              value={amount ? `${amount} ${formattedToken}` : ''}
              onCopy={handleCopyAmount}
              loading={!amount}
              valueStyle={{fontWeight: 'weight.normal'}}
            />
            <view.div css={{stack: 'y', gap: 'small'}}>
              <LabelValueCopyRow
                label={intl.formatMessage(messages.addressLabel)}
                value={depositAddress ?? ''}
                onCopy={handleCopy}
                loading={!depositAddress}
                valueStyle={{fontWeight: 'weight.normal'}}
              />
              {mobile ? countdown : null}
            </view.div>
          </view.div>

          {depositAddress ? (
            <BrandedButton onClick={handleCopy}>
              <view.div css={{stack: 'x', gap: 'small', alignY: 'center'}}>
                {isCopied ? null : (
                  <Icon
                    icon={clipboard}
                    css={{width: 'medium', height: 'medium'}}
                  />
                )}
                {intl.formatMessage(
                  isCopied ? messages.copied : messages.copyAddress,
                )}
              </view.div>
            </BrandedButton>
          ) : null}

          {onCheckPaymentStatus ? (
            <view.div css={{stack: 'y', alignItems: 'center'}}>
              <Action
                onPress={onCheckPaymentStatus}
                disabled={!amount}
                css={{
                  cursor: amount ? 'pointer' : 'not-allowed',
                }}
              >
                <Text
                  type="captionEmphasized"
                  css={{
                    color: 'text',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'dotted',
                  }}
                >
                  {intl.formatMessage(messages.checkPaymentStatus)}
                </Text>
              </Action>
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    </view.div>
  );
};
